import * as React from "react";
import IMyTradesList from "./IMyTradesList";
import { formatDate, numberWithDelimiter } from "@features/utils/functions";
import { IMyTradeTransaction } from "@market/pages/MyTradesPage/IMyTradesPage";
import { CURRENCY } from "@market/features/const.market";
import Big, { BigConstructor } from "big.js";
import ETradeTypes from "@market/enums/TradeTypes";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";

import * as style from "./MyTradesListStyle.module.scss";
import * as general from "@appStyles/general.module.scss";
import * as elements from "@appStyles/_elements.module.scss";

const tableHeaders = [
  "Name",
  "Date",
  "Type",
  "Status",
  "Price",
  "Amount",
  "Fee",
  "Total Amount",
  "Agreement",
];

export default class MyTradesList extends React.Component<IMyTradesList> {
  private big: BigConstructor = Big();

  totalAmmount = (trade: IMyTradeTransaction) => {
    return trade.fee_currency &&
      trade.fee_currency.toLowerCase() === CURRENCY.toLowerCase() ? (
      <CurrencyElement
        value={
          trade.type === ETradeTypes.buy
            ? this.big(trade.price_per_share)
                .times(trade.number_of_shares)
                .plus(trade.fee_amount)
            : this.big(trade.price_per_share)
                .times(trade.number_of_shares)
                .minus(trade.fee_amount)
        }
        currency={trade.fee_currency}
      />
    ) : (
      <React.Fragment>
        <CurrencyElement
          value={this.big(trade.price_per_share).times(trade.number_of_shares)}
          currency={trade.payment_currency}
        />
        {trade.fee_amount && (
          <React.Fragment>
            &nbsp;{trade.type === ETradeTypes.buy ? "+" : "-"}&nbsp;
            <CurrencyElement
              value={trade.fee_amount}
              currency={trade.fee_currency!}
            />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  getAgreement = (tradeId: number) => {
    const windowReference = window.open();

    SecondaryMarketService.getAgreement(tradeId).then((resp) => {
      if (windowReference) {
        windowReference.location = resp.data.url;
      }
    });
  };

  tradesData = () => {
    const { tradesList } = this.props;
    return tradesList.length > 0 ? (
      <div
        className={[elements.tableContainer, style.tableContainer].join(" ")}
      >
        <div className={elements.tableHead}>
          <div className={elements.tableHeadRow}>
            {tableHeaders.map((el: string, i: number) => {
              return (
                <div
                  key={i}
                  className={[elements.tableHeader, style.tradesHeader].join(
                    " "
                  )}
                >
                  {el}
                </div>
              );
            })}
          </div>
        </div>
        <div className={[elements.tableBody, style.tradesTable].join(" ")}>
          {tradesList.map((trade: IMyTradeTransaction) => {
            return (
              <div
                key={trade.id}
                className={[
                  elements.tableRow,
                  style.tradesRow,
                  `${
                    ["cancelled", "expired"].includes(trade.status)
                      ? general.rowExpired
                      : ""
                  }`,
                ].join(" ")}
              >
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeName,
                  ].join(" ")}
                >
                  {trade.spv_name}
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeDate,
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Date</span>
                  {formatDate(trade.created_at, true)}
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeType,
                    style[trade.type],
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Type</span>
                  {trade.type}
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeStatus,
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Status</span>
                  {trade.status}
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradePrice,
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Price</span>
                  <CurrencyElement
                    value={trade.price_per_share}
                    currency={trade.payment_currency}
                  />
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeAmmount,
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Amount</span>
                  <span className={general.noSpaceWrap}>
                    {numberWithDelimiter(trade.number_of_shares)}
                  </span>
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeFee,
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Fee</span>
                  {trade.fee_amount ? (
                    <CurrencyElement
                      value={trade.fee_amount}
                      currency={trade.fee_currency!}
                    />
                  ) : (
                    "-"
                  )}
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeTotal,
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Total amount</span>
                  <span className={general.noSpaceWrap}>
                    {" "}
                    {this.totalAmmount(trade)}{" "}
                  </span>
                </div>
                <div
                  className={[
                    elements.tableItem,
                    style.tradesItem,
                    style.tradeTotal,
                  ].join(" ")}
                >
                  <span className={elements.tableLabel}>Agreement</span>
                  {trade.agreement_generated ? (
                    <div className={style.agreement}>
                      <i className={style.iconAgreementWrapper}>
                        <svg
                          onClick={() => {
                            this.getAgreement(trade.id);
                          }}
                        >
                          <use xlinkHref={`#icon-document`} />
                        </svg>
                      </i>
                    </div>
                  ) : trade.status === "paid" ? (
                    <span>Processing ...</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ) : (
      <React.Fragment>
        <div className={style.iconContainer}>
          <svg className="svg-icon svg-icon--violet">
            <use xlinkHref="#icon-no-tokens" />
          </svg>
          <h4>Sorry, You don't have any transactions</h4>
        </div>
      </React.Fragment>
    );
  };

  render() {
    return this.tradesData();
  }
}
